import React, { useMemo } from 'react';
import { useAds } from '@moment/context/AdsContext.tsx';
import { DTXAd } from '../DTXAd/DTXAd';
import { AD_SIZE, BannerAdSizes } from '../GalleryAd/ad-constants.ts';
import './AdRenderer.scss';
import { useTranslation } from 'react-i18next';
import { isDntEnabled } from '@utils/dnt.ts';

/**
 * A generic ad renderer that handles all ad-related logic.
 * It determines which ad to show (GAM, DTX, or fallback) based on the central AdsContext.
 */
export interface AdRendererProps {
  /** A generic placement name for the ad slot, e.g., 'top' or 'bottom'. */
  placement: 'top' | 'bottom' | 'bot';
  /** The unique ad unit ID for this specific ad slot. */
  adUnitId: string;
  /** The pre-configured GAM component to render (e.g., <DisplayAd> or <GalleryAd>).
   * This makes the renderer flexible and decoupled from specific GAM implementations. */
  GamComponent: React.ReactElement;
  /** The required sizes for the DTX ad. */
  dtxAdSize: BannerAdSizes[];
  /** Optional class names for styling the DTX ad container. */
  containerClassName?: string;
  bannerClassName?: string;
  adBlockClassName?: string;
  iabCategories?: string[];
  dtxFallbackToMrecIfBanner?: boolean;
}

export const AdRenderer: React.FC<AdRendererProps> = ({
  placement,
  adUnitId,
  GamComponent,
  dtxAdSize,
  containerClassName,
  bannerClassName,
  adBlockClassName,
  iabCategories,
  dtxFallbackToMrecIfBanner = false,
}) => {
  const { t } = useTranslation();
  const { isReady, adConfigData, adSource } = useAds();
  const dntBlocked = isDntEnabled();

  // Memoize the calculation of the ad component. This prevents re-running the logic
  // and creating new component instances if the props and context data have not changed,
  // which is key to preventing unnecessary ad redraws.
  const adContent = useMemo(() => {
    if (adSource === 'none' || !adConfigData) {
      return null;
    }

    const standardizedPlacement = placement === 'bottom' ? 'bot' : placement;
    // 2561520 is default DTX placement ID
    const dtxPlacementId =
      adConfigData.adTags?.adTags?.[standardizedPlacement]?.placementID || '2561520';
    const dtxAdProps = {
      adUnitId,
      adSize: dtxAdSize,
      tagId: dtxPlacementId,
      containerClassName,
      adBlockClassName,
      iabCategories,
    };

    const both = () => {
      // first - banner, then fallback to middle and only then we GAM
      if (dtxFallbackToMrecIfBanner && dtxAdProps.adSize.length === 1 && dtxAdProps.adSize[0] === AD_SIZE.small) {
        const middleMan = (
          <DTXAd
            {...dtxAdProps}
            adSize={[AD_SIZE.middle]}
            fallbackAd={GamComponent}
          />
        );
        return <DTXAd {...dtxAdProps} fallbackAd={middleMan} />;
      } else {
        return <DTXAd {...dtxAdProps} fallbackAd={GamComponent} />;
      }
    }

    switch (adSource) {
      case 'gam':
        return GamComponent;
      case 'dtx':
        return <DTXAd {...dtxAdProps} fallbackAd={null} />;
      case 'dtx,gam':
        return both();
      default:
        console.error(`Unknown ad source: ${adSource}`);
        return both();
    }
  }, [
    adSource,
    adConfigData,
    placement,
    adUnitId,
    GamComponent,
    dtxAdSize,
    containerClassName,
    adBlockClassName,
    iabCategories,
  ]);

  const contentToRender = useMemo(
    () => (isReady ? adContent : null),
    [isReady]
  );

  const getAdSizeHeight = useMemo(() => {
    // add 30px for advertisement" banner
    return (
      (dtxAdSize
        ?.map((size) => size.height)
        .sort()
        .reverse()
        .pop() || 250) + 30
    );
  }, [dtxAdSize]);

  if (dntBlocked) {
    return null;
  }

  return (
    <div
      className={`ad-renderer-container`}
      style={{ minHeight: `${getAdSizeHeight}px` }}
    >
      <div className={`ad--title ${bannerClassName}`}>{t('advertisement')}</div>
      {contentToRender}
    </div>
  );
};
